import { useMemo } from 'react'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { getMatrixData } from 'components/spreadsheet/helpers/matrix'
import { getTransposedData } from 'components/spreadsheet/helpers/transpose'
import { ISpreadsheetData } from 'components/spreadsheet/types'
import { createMatrixAdapter } from './matrixAdapter'
import { createSpreadsheetAdapter } from './spreadsheetAdapter'
import { createTransposeAdapter } from './transposeAdapter'
import { ViewAdapterResult } from './types'

export function useViewAdapter(spreadsheetData: ISpreadsheetData): ViewAdapterResult {
  // Memoised by hand: the transpose and matrix passes walk every row, and the data provider that
  // calls this re-renders (with a new spreadsheetData object) on every state change. The compiler
  // would key the pass on that object; these keys are the inputs the pass actually reads, so
  // selecting a cell or editing one does not pivot a large table again. The adapter built from the
  // result is memoised too: its effective data object goes into the data context value, which must
  // only change when the data does, not on the provider's scroll-frame renders.
  'use no memo'
  const viewType = spreadsheetData.viewDetails.type
  const pivotColumnId = spreadsheetData.viewDetails.transposeSettings?.pivotColumnId ?? null
  const shouldTranspose = viewType === ViewTypes.TRANSPOSE && !!pivotColumnId && !spreadsheetData.loading

  const matrixSettings = spreadsheetData.viewDetails.matrixSettings ?? null
  const shouldMatrix =
    viewType === ViewTypes.MATRIX &&
    !!matrixSettings?.pivotColumnId &&
    matrixSettings.rowLabelColumnIds.length > 0 &&
    matrixSettings.dataFieldColumnIds.length > 0 &&
    !spreadsheetData.loading

  const { rows, visibleBitmap, columnStore, columnIdToIndex, viewDetails, userConfiguration } = spreadsheetData
  const { columns, transposeSettings } = viewDetails
  const { hiddenColumns } = userConfiguration

  const transposedResult = useMemo(
    () => (shouldTranspose ? getTransposedData(spreadsheetData, pivotColumnId!) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on what getTransposedData reads
    [
      shouldTranspose,
      pivotColumnId,
      rows,
      visibleBitmap,
      columnStore,
      columnIdToIndex,
      columns,
      hiddenColumns,
      transposeSettings
    ]
  )

  const matrixResult = useMemo(
    () => (shouldMatrix ? getMatrixData(spreadsheetData, matrixSettings) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on what getMatrixData reads
    [shouldMatrix, matrixSettings, rows, visibleBitmap, columnStore, columnIdToIndex, columns]
  )

  return useMemo(() => {
    if (transposedResult) {
      return createTransposeAdapter(spreadsheetData, transposedResult)
    }

    if (matrixResult && matrixResult.columns.length > 0) {
      return createMatrixAdapter(spreadsheetData, matrixResult)
    }

    // Add future view types here with their own adapters as needed

    return createSpreadsheetAdapter(spreadsheetData)
  }, [spreadsheetData, transposedResult, matrixResult])
}
